import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Box,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import axios from "../services/axiosInstance"; // ✅ Add this
import API_BASE_URL from "../config"; // ✅ And this

import {
  fetchUsers,
  updateUser,
  deleteUser,
} from "../services/userService";

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [openDialog, setOpenDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    user_id:"",
    password: "",
    phone: "",
    city: "",
    role: "user",
  });

  useEffect(() => {
    async function loadUsers() {
      setLoading(true);
      try {
        const response = await fetchUsers();
        setUsers(response?.users ?? []);
      } catch (error) {
        console.error("❌ Error fetching users:", error);
      }
      setLoading(false);
    }
    loadUsers();
  }, []);

 // ✅ Handle Role Change
 const handleRoleChange = async (id, newRole) => {
  try {
    const updatedUser = await updateUser(id, { role: newRole });
    if (updatedUser) {
      setUsers((prev) =>
        prev.map((user) => (user.id === id ? { ...user, role: newRole } : user))
      );
    }
  } catch (error) {
    console.error("❌ Error updating role:", error);
  }
};

// ✅ Toggle User Status (Enable/Disable)
const handleStatusToggle = async (id, currentStatus) => {
  const newStatus = currentStatus?.toLowerCase() === "active" ? "disabled" : "active";
  try {
    const updatedUser = await updateUser(id, { status: newStatus });
    if (updatedUser) {
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, status: newStatus } : user
        )
      );
    }
  } catch (error) {
    console.error("❌ Error toggling user status:", error);
  }
};

// ✅ Handle User Deletion
const handleDeleteUser = async (id) => {
  if (window.confirm("Are you sure you want to delete this user?")) {
    try {
      const response = await deleteUser(id);
      if (response) {
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
      }
    } catch (error) {
      console.error("❌ Error deleting user:", error);
    }
  }
};

  // ✅ Updated create user function with correct POST logic
  const handleCreateUser = async () => {
    if (
      !newUser.name ||
      !newUser.email ||
      !newUser.user_id ||
      !newUser.password ||
      !newUser.phone ||
      !newUser.city 
    ) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/users/register`, {
        ...newUser,
        post_job: false,
        post_service: false,
        post_ad: false,
        post_offer: false,
        post_event: false,
        post_classified: false,
      });

      if (response?.data) {
        setUsers((prevUsers) => [...prevUsers, response.data]);
        setOpenDialog(false);
        setNewUser({
          name: "",
          email: "",
          user_id:"",
          password: "",
          phone: "",
          city: "",
          role: "user",
        });
      }
    } catch (error) {
      console.error("❌ Error creating user:", error.response?.data || error.message);
      alert("User creation failed. Please check the details and try again.");
    }
  };

  const filteredUsers = users
    .filter((user) => user?.name?.toLowerCase()?.includes(search.toLowerCase()))
    .filter((user) =>
      roleFilter === "All" ? true : user?.role === roleFilter
    );

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        textAlign="center"
        mb={3}
        color="#42326e"
      >
        Admin User Management
      </Typography>

      {loading ? (
        <CircularProgress sx={{ display: "block", margin: "auto" }} />
      ) : (
        <>
          {/* Search & Filters */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
              mb: 3,
              justifyContent: "space-between",
            }}
          >
            <TextField
              size="medium"
              label="Search User"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: "30%" }}
            />
            <TextField
              select
              label="Filter By Role"
              variant="outlined"
              sx={{ width: "30%" }}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>

            <Button
              variant="contained"
              sx={{
                width: "30%",
                backgroundColor: "#42326e",
                color: "White",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              onClick={() => setOpenDialog(true)}
            >
              <AddIcon /> Create User
            </Button>
          </Box>

          {/* User Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>Name</TableCell>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>Email</TableCell>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>Phone</TableCell>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>City</TableCell>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>Role</TableCell>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>Status</TableCell>
                  <TableCell sx={{color:"#42326e",fontSize:"1rem",fontWeight:"bold"}}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user?.id}>
                    <TableCell>{user?.name || "N/A"}</TableCell>
                    <TableCell>{user?.email || "N/A"}</TableCell>
                    <TableCell>{user?.phone || "N/A"}</TableCell>
                    <TableCell>{user?.city || "N/A"}</TableCell>
                    <TableCell>
                      <Select
                        value={user?.role || "user"}
                        onChange={(e) =>
                          handleRoleChange(user.id, e.target.value)
                        }
                        size="small"
                      >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={user?.status === "active"}
                        onChange={() =>
                          handleStatusToggle(user?.id, user?.status)
                        }
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteUser(user?.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Create User Dialog */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle
              sx={{
                color: "#42326e",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "2rem",
              }}
            >
              Add New User
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Name"
                fullWidth
                sx={{ mt: 2, mb: 2 }}
                value={newUser.name}
                onChange={(e) =>
                  setNewUser({ ...newUser, name: e.target.value })
                }
              />
              <TextField
                label="Email"
                fullWidth
                sx={{ mb: 2 }}
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
              />
              <TextField
                label="UserId"
                fullWidth
                sx={{ mb: 2 }}
                value={newUser.user_id}
                onChange={(e) =>
                  setNewUser({ ...newUser, user_id: e.target.value })
                }
              />
              <TextField
                label="Password"
                fullWidth
                type="password"
                sx={{ mb: 2 }}
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
              />
              <TextField
                label="Phone"
                fullWidth
                sx={{ mb: 2 }}
                value={newUser.phone}
                onChange={(e) =>
                  setNewUser({ ...newUser, phone: e.target.value })
                }
              />
              <TextField
                label="City"
                fullWidth
                sx={{ mb: 2 }}
                value={newUser.city}
                onChange={(e) =>
                  setNewUser({ ...newUser, city: e.target.value })
                }
              />
            </DialogContent>
            <DialogActions sx={{ mb: 2, mx: 2 }}>
              <Button
                onClick={() => setOpenDialog(false)}
                sx={{ color: "white", backgroundColor: "darkred" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                sx={{ color: "white", backgroundColor: "#0653a2" }}
              >
                Create
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default AdminUserManagement;
